// workshop-suggestion.ts — PRODHUIT suggestion for small adhesive volumes.
//
// Detects adhesive-category materials whose cumulative m² on the quote is strictly under 10
// and exposes the suggestion (materials, cumuls, per-line status) for the web popup.
// Indicative only and stateless: writes workshopResp only, never PV / minimums / discounts.

import { engineSettings } from "../../data";
import type { QuoteState } from "../compute/compute";
import type { QuoteLine } from "../core/core";
import { isLargeFormatPrintRegime } from "../core/regimes";
import { cumulateByCategory, volumeCategory } from "../pricing/volume-tiers";
import { materialFamilyOf } from "../catalogue/compatibility";
import { defaultWorkshop, lineWorkshopMenu } from "./workshops";
import { paMode, paSupplierLookup } from "../pricing/pa-margins";

/** Workshop label for PRODHUIT (resolves the SERVICE_KEY "PRODHUIT" entry). */
export const PRODHUIT_LABEL = "PRODHUIT (Odillon)";

/**
 * Small-volume threshold in m² (setting `smallVolumeThresholdM2`): a material's cumulative m² must be strictly under it.
 * @returns The threshold in m2.
 */
export function smallVolumeThresholdM2(): number {
  return engineSettings().parameters.smallVolumeThresholdM2;
}

/**
 * Status of a line with respect to the PRODHUIT switch: `switchTo` (will switch),
 * `alreadyProdhuit` (nothing to do), `manualChoice` (kept or overwritten per action),
 * `outOfCapacity` (PRODHUIT not capable, excluded but reported).
 */
export type SuggestedLineStatus = "switchTo" | "alreadyProdhuit" | "manualChoice" | "outOfCapacity";

export interface SuggestedWorkshopLine {
  /** Index of the line in `state.lines` (apply key for the web layer). */
  index: number;
  /** Display number of the line. */
  num: number;
  material: string;
  /** Workshop currently carried by the line (workshopResp if set, else resolved default). */
  currentWorkshop: string;
  status: SuggestedLineStatus;
  /** True when PRODHUIT has no PA grid key for this combination, so the switch falls back to target PA. */
  paNotCollected: boolean;
}

export interface SuggestedMaterial {
  /** Cumul key (`volumeCategory`) — groups UPZ stock, twin and mounting like the tiers do. */
  volumeKey: string;
  /** Representative material ref (first line of the group). */
  material: string;
  /** Cumulative m² of the material on the quote (exact, unrounded, like the tier base). */
  cumulatedM2: number;
  lines: SuggestedWorkshopLine[];
}

export interface ProdhuitSuggestion {
  /** True when the popup should show: at least one switchable line on an adhesive material under 10 m². */
  active: boolean;
  /** True when at least one affected line carries a manual choice, giving the popup 3 actions instead of 2. */
  hasManualChoice: boolean;
  materials: SuggestedMaterial[];
}

/**
 * Is the material in an adhesive category (AD_* families)? Resolved from the referential, never hardcoded.
 *
 * @param material The material ref.
 * @returns True for an adhesive material.
 */
export function isAdhesiveMaterial(material: string | undefined): boolean {
  return !!materialFamilyOf(material)?.startsWith("AD_");
}

/**
 * Is PRODHUIT capable for the line's resolved family (rank 1/2 or allowed)?
 *
 * @param l The quote line.
 * @returns True when PRODHUIT is in the line's workshop menu.
 */
export function isProdhuitCapable(l: QuoteLine): boolean {
  return lineWorkshopMenu(l).options.some((o) => o.label === PRODHUIT_LABEL);
}

/**
 * Workshop effectively carried by the line: workshopResp if set, else the resolved default.
 *
 * @param l The quote line.
 * @returns The workshop label.
 */
function workshopCarries(l: QuoteLine): string {
  const responsible = (l.workshopResp ?? "").trim();
  return responsible || defaultWorkshop(l);
}

/**
 * Manual choice? The model has no such flag, so the signal is workshopResp set and different from the default.
 *
 * @param l The quote line.
 * @returns True when the workshop was chosen by hand.
 */
function isManualChoice(l: QuoteLine): boolean {
  const responsible = (l.workshopResp ?? "").trim();
  return responsible !== "" && responsible !== defaultWorkshop(l);
}

/**
 * Does the line's combination have a PRODHUIT PA grid key? Otherwise the switch falls back to target PA.
 *
 * @param l The quote line.
 * @returns True when a PA grid key exists for PRODHUIT.
 */
function hasProdhuitPaGrid(l: QuoteLine): boolean {
  const probe: QuoteLine = { ...l, workshopResp: PRODHUIT_LABEL };
  return paSupplierLookup(probe, paMode(probe)) != null;
}

/**
 * Small-volume PRODHUIT suggestion: groups adhesive PRINT_GF lines by cumul key, keeps groups
 * strictly under 10 m², classifies each line and returns the popup payload. `active` stays true
 * only while a switchable line remains, so the popup cannot reappear in a loop.
 *
 * @param state The quote state (its lines are read).
 * @returns The suggestion payload for the popup.
 */
export function suggestProdhuit(state: QuoteState): ProdhuitSuggestion {
  const lines = state.lines ?? [];
  const cumulByKey = cumulateByCategory(lines); // cumul key to m², same source as the tiers

  // Group the indexes of adhesive lines by cumul key.
  const byKey = new Map<string, number[]>();
  lines.forEach((l, i) => {
    if (!isLargeFormatPrintRegime(l) || !isAdhesiveMaterial(l.material)) return;
    const key = volumeCategory(l);
    if (key == null) return;
    let indexList = byKey.get(key);
    if (!indexList) byKey.set(key, (indexList = []));
    indexList.push(i);
  });

  const materials: SuggestedMaterial[] = [];
  let hasManualChoice = false;
  let toSwitch = false;

  for (const [key, indexes] of byKey) {
    const cumulatedM2 = cumulByKey.get(key) ?? 0;
    if (!(cumulatedM2 < smallVolumeThresholdM2())) continue; // strict bound: exactly the threshold is out of scope

    const suggestedLines: SuggestedWorkshopLine[] = indexes.map((i) => {
      const l = lines[i]!;
      let status: SuggestedLineStatus;
      if (!isProdhuitCapable(l)) status = "outOfCapacity";
      else if (workshopCarries(l) === PRODHUIT_LABEL) status = "alreadyProdhuit";
      else if (isManualChoice(l)) status = "manualChoice";
      else status = "switchTo";
      if (status === "manualChoice") hasManualChoice = true;
      if (status === "switchTo") toSwitch = true;
      return {
        index: i,
        num: l.num ?? i + 1,
        material: l.material ?? "",
        currentWorkshop: workshopCarries(l),
        status,
        paNotCollected: status !== "outOfCapacity" && !hasProdhuitPaGrid(l),
      };
    });

    materials.push({ volumeKey: key, material: lines[indexes[0]!]!.material ?? "", cumulatedM2, lines: suggestedLines });
  }

  return { active: toSwitch, hasManualChoice, materials };
}

/**
 * Applies PRODHUIT to the affected lines and returns a new array (immutable).
 * `exceptManual` keeps manual choices, `everywhere` overwrites them. Out-of-capacity and
 * already-PRODHUIT lines are never touched; only workshopResp is written.
 *
 * @param lines The quote lines.
 * @param suggestion The suggestion computed by `suggestProdhuit`.
 * @param mode Whether manual choices are kept or overwritten.
 * @returns A new array of lines.
 */
export function applySuggestion(
  lines: readonly QuoteLine[],
  suggestion: ProdhuitSuggestion,
  mode: "exceptManual" | "everywhere",
): QuoteLine[] {
  const toSwitch = new Set<number>();
  for (const m of suggestion.materials)
    for (const suggestedLine of m.lines) {
      if (suggestedLine.status === "switchTo") toSwitch.add(suggestedLine.index);
      else if (suggestedLine.status === "manualChoice" && mode === "everywhere") toSwitch.add(suggestedLine.index);
    }
  return lines.map((l, i) => (toSwitch.has(i) ? { ...l, workshopResp: PRODHUIT_LABEL } : l));
}
